import { useEffect, useCallback, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import CustomMarker from '../map_interaction/CustomMarker';
import { usePlaceContext } from '../itinerary/PlaceContext';
import { isEqual } from 'lodash';

const useSuggestionMarkers = (
  googleMapsAPI,
  googleMap,
  suggestions,
  onMarkersAdded,
  placesService,
  fetchPlaceId,
  fetchPlaceDetailsWrapper,
  showPlaceInfoBox
) => {
  const markersRef = useRef(new Map());
  const previousSuggestionsRef = useRef(null);
  const { setExploreOriginId } = usePlaceContext();

  const clearMarkers = useCallback(() => {
    if (!googleMap) return;
    markersRef.current.forEach((marker) => {
      marker.setMap(null);
      if (marker.div && marker.div.parentNode) {
        marker.div.parentNode.removeChild(marker.div);
      }
    });
    markersRef.current.clear();
  }, [googleMap]);

  const createMarker = useCallback(
    (suggestion) => {
      if (!googleMap) return;
      const {
        suggestion_latitude: lat,
        suggestion_longitude: lng,
        suggestion_name: label,
        origin_id,
        place_id,
        markerColor,
        itinerary_id
      } = suggestion;
      const markerKey = itinerary_id ? `${label}-${lat}-${lng}` : origin_id;
      if (markersRef.current.has(markerKey)) {
        return markersRef.current.get(markerKey);
      }
      const customMarker = new googleMapsAPI.OverlayView();
      customMarker.onAdd = function () {
        const div = document.createElement('div');
        div.style.position = 'absolute';
        div.innerHTML = ReactDOMServer.renderToString(
          <CustomMarker label={label} color={markerColor} type="suggestion" />
        );
        div.onclick = async (e) => {
          e.stopPropagation();
          setExploreOriginId(origin_id);
          if (placesService && fetchPlaceDetailsWrapper && place_id) {
            const placeDetails = await fetchPlaceDetailsWrapper(place_id);
            if (placeDetails) {
              showPlaceInfoBox(placeDetails);
            }
          } else {
            console.error('Missing placesService, fetchPlaceDetails, or place_id');
          }
        };
        this.div = div;
        const panes = this.getPanes();
        panes.overlayMouseTarget.appendChild(div);
      };
      customMarker.draw = function () {
        if (this.div) {
          const projection = this.getProjection();
          const position = projection.fromLatLngToDivPixel(
            new googleMapsAPI.LatLng(lat, lng)
          );
          if (position) {
            this.div.style.left = `${position.x}px`;
            this.div.style.top = `${position.y}px`;
          }
        }
      };
      customMarker.onRemove = function () {
        if (this.div) {
          this.div.parentNode.removeChild(this.div);
          this.div = null;
        }
      };
      customMarker.setMap(googleMap);
      markersRef.current.set(markerKey, customMarker);
      return customMarker;
    },
    [googleMap, setExploreOriginId, placesService, fetchPlaceDetailsWrapper, showPlaceInfoBox, googleMapsAPI]
  );

  const addMarkers = useCallback(() => {
    if (!googleMap || !suggestions.length) return;
    suggestions.forEach((suggestion) => createMarker(suggestion));
    if (onMarkersAdded) {
      onMarkersAdded();
    }
  }, [googleMap, suggestions, createMarker, onMarkersAdded]);

  useEffect(() => {
    if (!googleMap || !suggestions.length || !placesService) {
      return;
    }
    const suggestionsChanged = !isEqual(previousSuggestionsRef.current, suggestions);
    if (!suggestionsChanged) {
      return;
    }
    const suggestionsWithoutPlaceId = suggestions.filter(
      (suggestion) => !suggestion.place_id
    );
    if (suggestionsWithoutPlaceId.length === 0) {
      clearMarkers();
      addMarkers();
      previousSuggestionsRef.current = suggestions;
      return;
    }
    const fetchPlaceIds = async () => {
      for (const suggestion of suggestionsWithoutPlaceId) {
        const placeId = await fetchPlaceId(placesService, suggestion);
        if (placeId) {
          suggestion.place_id = placeId;
        } else {
          console.warn(`No place_id found for suggestion: ${suggestion.suggestion_name}`);
        }
      }
      clearMarkers();
      addMarkers();
    };
    fetchPlaceIds();
    previousSuggestionsRef.current = suggestions;
  }, [googleMap, suggestions, placesService, clearMarkers, addMarkers, fetchPlaceId]);

  useEffect(() => {
    if (googleMap && placesService && suggestions.length === 0) {
      clearMarkers();
      previousSuggestionsRef.current = [];
    }
  }, [googleMap, placesService, suggestions, clearMarkers]);

  return { markers: Array.from(markersRef.current.values()) };
};

export default useSuggestionMarkers;
